import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '../../state/app.state';
import * as IcpActions from '../../state/icp/icp.actions';
import * as IcpSelectors from '../../state/icp/icp.selectors';
import { HeaderService } from '../../services/header.service';
import { PopupService } from '../../services/popup.service';
import { ICP } from '../../state/icp/icp.state';
import { DarkModeToggleComponent } from '../../common/dark-mode-toggle/dark-mode-toggle.component';
import { CrmPaginationComponent } from '../crm/crm-pagination/crm-pagination.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-icp',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    DarkModeToggleComponent,
    CrmPaginationComponent
  ],
  templateUrl: './icp.component.html',
  styleUrl: './icp.component.scss'
})
export class IcpComponent implements OnInit, OnDestroy {
  icps$ = this.store.select(IcpSelectors.selectIcps);
  loading$ = this.store.select(IcpSelectors.selectIcpsLoading);
  error$ = this.store.select(IcpSelectors.selectIcpsError);
  pagination$ = this.store.select(IcpSelectors.selectIcpsPagination);

  private destroy$ = new Subject<void>();

  // Local state
  paginatedIcps: ICP[] = [];
  tableHeight: number = 0;
  pagination: any = {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 10
  };

  // Pagination variables
  currentPage = 1;
  perPage = 10;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private headerService: HeaderService,
    private popupService: PopupService
  ) {}

  ngOnInit(): void {
    this.headerService.setHeaderState('ICP', null, true);

    // Load ICPs
    this.loadIcps();

    // Subscribe to pagination changes
    this.pagination$.pipe(takeUntil(this.destroy$)).subscribe(pagination => {
      if (pagination) {
        this.currentPage = pagination.current_page;
        this.perPage = pagination.per_page;
        this.pagination = pagination;
      }
    });

    // Subscribe to ICPs data
    this.icps$.pipe(takeUntil(this.destroy$)).subscribe(icps => {
      this.paginatedIcps = icps || [];
    });

    // Calculate initial table height
    this.updateTableHeight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateTableHeight();
  }

  updateTableHeight(): void {
    const headerHeight = 80; // ICP header
    const actionsHeight = 36; // Actions button area height (no tabs)
    const paginationHeight = 36; // Pagination component height
    const padding = 112; // Various paddings

    this.tableHeight = window.innerHeight - headerHeight - actionsHeight - paginationHeight - padding;
  }

  loadIcps(): void {
    this.store.dispatch(IcpActions.loadIcps({
      page: this.currentPage,
      perPage: this.perPage,
      include: ['workspaces']
    }));
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadIcps();
  }

  onPageChanged(page: number): void {
    this.store.dispatch(IcpActions.loadIcps({
      page: page,
      perPage: this.pagination.per_page,
      include: ['workspaces']
    }));
  }

  onPerPageChanged(perPage: number): void {
    this.store.dispatch(IcpActions.loadIcps({
      page: 1,
      perPage: perPage,
      include: ['workspaces']
    }));
  }

  onCreateIcp(): void {
    this.router.navigate(['/icp/create']);
  }

  onEditIcp(icp: ICP): void {
    this.router.navigate(['/icp/edit', icp.id]);
  }

  onDeleteIcp(icp: ICP): void {
    // Block deletion of default ICP
    if (icp.is_default) {
      Swal.fire({
        title: 'Cannot Delete Default ICP',
        html: `
          <div class="text-sm text-gray-700">
            <p class="mb-2">Your default ICP cannot be deleted.</p>
            <p class="text-xs text-gray-500">This is the first ICP you created and serves as your primary Ideal Customer Profile.</p>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626',
        animation: false,
        customClass: {
          popup: 'swal2-popup-custom'
        }
      });
      return; // Exit early - do not allow deletion
    }

    // Allow deletion for non-default ICPs
    Swal.fire({
      title: 'Delete ICP',
      html: `
        <div class="text-sm text-gray-700">Are you sure you want to delete "${icp.name}"?</div>
        <div class="text-xs text-gray-500 mt-3">This action cannot be undone.</div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      animation: false,
      customClass: {
        popup: 'swal2-popup-custom'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.store.dispatch(IcpActions.deleteIcp({ id: icp.id }));
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getStatusBadgeClass(icp: ICP): string {
    return icp.is_default
      ? 'bg-violet-500/10 text-violet-500'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }

  onViewIcp(icp: ICP): void {
    this.router.navigate(['/icp/detail', icp.id]);
  }

  onDuplicateIcp(icp: ICP): void {
    this.store.dispatch(IcpActions.cloneIcp({ id: icp.id }));
  }

}
